import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Gift,
  Settings,
  Headset,
  LayoutGrid,
  LogOut as LogOutIcon,
} from "lucide-react";
import { setToggleMenu } from "../../server/CartsOpen";
import { getCategoryNavIconComponent } from "./categoryNavIcons";
import { isCategoryVisible } from "../../utils/categoryVisibility";
import { logout } from "../../utils/authUtils";
import CustomerService from "../customerService/CustomerService";
import "./MobileNavDrawer.scss";

const MobileNavDrawer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Drill-down path through the category tree (one node per level).
  const [stack, setStack] = useState([]);

  const toggleMenu = useSelector((state) => state.openCart.toggleMenu);
  const categories = useSelector((state) => state.category.categories);
  const role = useSelector((state) => state.user.role);
  const includeInactive = useSelector(
    (state) => state.category.includeInactive,
  );

  if (!toggleMenu) return null;

  const close = () => {
    dispatch(setToggleMenu());
    setStack([]);
  };

  const navigateTo = (path) => {
    navigate(path);
    close();
  };

  // Admin "ניהול" section reuses the same drill-down stack via kind-tagged nodes.
  const back = () => setStack((s) => s.slice(0, -1));
  const openManage = () =>
    setStack((s) => [...s, { kind: "manage", name: "ניהול" }]);
  const openService = () =>
    setStack((s) => [...s, { kind: "service", name: "שירות לקוחות" }]);
  const openCategories = () =>
    setStack((s) => [...s, { kind: "categories", name: "קטגוריות" }]);
  const handleLogout = () => {
    logout({ dispatch, navigate });
    close();
  };

  // Children of a node that are navigable sub-categories (same rule the desktop
  // SubNavbar uses to decide whether to render a deeper level).
  const navigableChildren = (cat) =>
    (cat?.children ?? []).filter((c) =>
      isCategoryVisible(c, role, includeInactive),
    );

  const visibleCategories = (categories ?? []).filter(
    (cat) =>
      cat.status === "ACTIVE" &&
      cat.totalProducts > 0 &&
      (!includeInactive || !role ? cat.activeProducts > 0 : true),
  );

  const current = stack[stack.length - 1] ?? null;
  // At the root we list the top-level categories; once drilled in we list the
  // current node's navigable children.
  const childItems = current ? navigableChildren(current) : null;

  const onItemClick = (cat) => {
    if (navigableChildren(cat).length > 0) {
      setStack((s) => [...s, cat]);
    } else {
      navigateTo(`/category/${cat.slug}`);
    }
  };

  const renderCategoryItem = (cat, index) => {
    const CategoryIcon = getCategoryNavIconComponent(cat.slug, cat.name, index);
    return (
      <li key={cat.slug}>
        <button
          type="button"
          className="mobile-nav-drawer__item"
          onClick={() => onItemClick(cat)}
        >
          <span className="mobile-nav-drawer__item-icon">
            <CategoryIcon size={20} strokeWidth={1.8} aria-hidden />
          </span>
          <span className="mobile-nav-drawer__item-label">{cat.name}</span>
          <ChevronLeft
            size={16}
            className="mobile-nav-drawer__chevron"
            aria-hidden
          />
        </button>
      </li>
    );
  };

  return (
    <div
      className="mobile-nav-drawer"
      dir="rtl"
      aria-modal="true"
      role="dialog"
    >
      <div className="mobile-nav-drawer__backdrop" onClick={close} />

      <div className="mobile-nav-drawer__panel">
        {/* Header row */}
        <div className="mobile-nav-drawer__head">
          {current ? (
            <button
              type="button"
              className="mobile-nav-drawer__back"
              onClick={() => setStack((s) => s.slice(0, -1))}
              aria-label="חזרה"
            >
              <ChevronRight size={22} strokeWidth={2} />
            </button>
          ) : (
            <span className="mobile-nav-drawer__title">תפריט</span>
          )}
          {current && (
            <span className="mobile-nav-drawer__title mobile-nav-drawer__title--crumb">
              {current.name}
            </span>
          )}
          <button
            type="button"
            className="mobile-nav-drawer__close"
            onClick={close}
            aria-label="סגור"
          >
            <X size={22} strokeWidth={2} />
          </button>
        </div>

        {/* Content area */}
        <div className="mobile-nav-drawer__content">
          {!current ? (
            /* Root level — top categories (with icons) */
            <ul className="mobile-nav-drawer__list" key="root">
              {role && (
                <li>
                  <button
                    type="button"
                    className="mobile-nav-drawer__item mobile-nav-drawer__item--manage"
                    onClick={openManage}
                  >
                    <span className="mobile-nav-drawer__item-icon">
                      <Settings size={20} strokeWidth={1.8} aria-hidden />
                    </span>
                    <span className="mobile-nav-drawer__item-label">ניהול</span>
                    <ChevronLeft
                      size={16}
                      className="mobile-nav-drawer__chevron"
                      aria-hidden
                    />
                  </button>
                </li>
              )}
              {role && (
                <li>
                  <button
                    type="button"
                    className="mobile-nav-drawer__item"
                    onClick={openService}
                  >
                    <span className="mobile-nav-drawer__item-icon">
                      <Headset size={20} strokeWidth={1.8} aria-hidden />
                    </span>
                    <span className="mobile-nav-drawer__item-label">
                      שירות לקוחות
                    </span>
                    <ChevronLeft
                      size={16}
                      className="mobile-nav-drawer__chevron"
                      aria-hidden
                    />
                  </button>
                </li>
              )}
              <li>
                <button
                  type="button"
                  className="mobile-nav-drawer__item mobile-nav-drawer__item--sales"
                  onClick={() => navigateTo("/sales")}
                >
                  <span className="mobile-nav-drawer__item-icon" aria-hidden>
                    <Gift size={20} strokeWidth={1.8} aria-hidden />
                  </span>
                  <span className="mobile-nav-drawer__item-label">מבצעים</span>
                  <ChevronLeft
                    size={16}
                    className="mobile-nav-drawer__chevron"
                    aria-hidden
                  />
                </button>
              </li>
              {role ? (
                /* Admin: collapse all categories behind one entry so the
                   logout below stays reachable without scrolling. */
                <li>
                  <button
                    type="button"
                    className="mobile-nav-drawer__item"
                    onClick={openCategories}
                  >
                    <span className="mobile-nav-drawer__item-icon">
                      <LayoutGrid size={20} strokeWidth={1.8} aria-hidden />
                    </span>
                    <span className="mobile-nav-drawer__item-label">
                      קטגוריות
                    </span>
                    <ChevronLeft
                      size={16}
                      className="mobile-nav-drawer__chevron"
                      aria-hidden
                    />
                  </button>
                </li>
              ) : (
                visibleCategories.map(renderCategoryItem)
              )}
              {role && (
                <li>
                  <button
                    type="button"
                    className="mobile-nav-drawer__item"
                    onClick={handleLogout}
                  >
                    <span className="mobile-nav-drawer__item-icon">
                      <LogOutIcon size={20} strokeWidth={1.8} aria-hidden />
                    </span>
                    <span className="mobile-nav-drawer__item-label">יציאה</span>
                  </button>
                </li>
              )}
            </ul>
          ) : current.kind === "manage" ? (
            /* Admin "ניהול" actions */
            <ul className="mobile-nav-drawer__list" key="manage">
              <li>
                <button
                  type="button"
                  className="mobile-nav-drawer__item"
                  onClick={() => navigateTo("/admin/users")}
                >
                  <span className="mobile-nav-drawer__item-icon">
                    <Settings size={20} strokeWidth={1.8} aria-hidden />
                  </span>
                  <span className="mobile-nav-drawer__item-label">הגדרות</span>
                  <ChevronLeft
                    size={16}
                    className="mobile-nav-drawer__chevron"
                    aria-hidden
                  />
                </button>
              </li>
            </ul>
          ) : current.kind === "categories" ? (
            /* All top-level categories (admin: grouped under one entry) */
            <ul className="mobile-nav-drawer__list" key="categories">
              {visibleCategories.map(renderCategoryItem)}
            </ul>
          ) : current.kind === "service" ? (
            /* Customer-service contact panel (reuses the header component + styles) */
            <div
              className="mobile-nav-drawer__service customer-service-menu"
              key="service"
            >
              <CustomerService setOpenMenu={back} />
            </div>
          ) : (
            /* Drilled-in level — "view all" + this node's sub-categories */
            <ul className="mobile-nav-drawer__list" key={current.slug}>
              <li>
                <button
                  type="button"
                  className="mobile-nav-drawer__item mobile-nav-drawer__item--view-all"
                  onClick={() => navigateTo(`/category/${current.slug}`)}
                >
                  <span className="mobile-nav-drawer__item-label">
                    כל {current.name}
                  </span>
                  <ChevronLeft
                    size={16}
                    className="mobile-nav-drawer__chevron"
                    aria-hidden
                  />
                </button>
              </li>
              {childItems.map((child) => (
                <li key={child.slug}>
                  <button
                    type="button"
                    className="mobile-nav-drawer__item"
                    onClick={() => onItemClick(child)}
                  >
                    <span className="mobile-nav-drawer__item-label">
                      {child.name}
                    </span>
                    <ChevronLeft
                      size={16}
                      className="mobile-nav-drawer__chevron"
                      aria-hidden
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileNavDrawer;
